/**
 * Cached facet fetching utilities.
 *
 * Fetches facet values directly from Algolia with unstable_cache
 * for ISR-equivalent time-based revalidation (1 hour).
 *
 * Previously these went through the /api/facets/[locale] endpoint
 * which caused unnecessary server-to-self HTTP round-trips on Vercel.
 *
 * @TODO [BEC-000] Evaluate removing unstable_cache in favour of React.cache()
 * for per-request deduplication only. Algolia responds in <50ms for
 * facet-only queries (hitsPerPage: 0), so the 1-hour TTL saves little
 * latency while risking stale filter data after index updates. The
 * module-level `requestCache` Map also never clears between requests in
 * a long-lived server process and should be replaced.
 */

import { unstable_cache } from 'next/cache';

import { devLog, devWarn } from '@/lib/utils/devLog';
import { getAlgoliaClient, getProductIndexName } from './client';
import { ALGOLIA_CONFIG, getLocaleCode, getLocaleCodeCandidates } from './config';
import { algoliaContextFields, logAlgoliaRequest, logAlgoliaResponse } from './debugLog';
import { getEyewearFacets, getStrapFacets, getWatchFacets } from './facets';
import { buildFilterString } from './filters';
import type { FacetResults, StrapHit, WatchHit } from './types';

const FACETS_REVALIDATE = 3600; // 1 hour, matching previous ISR config

/**
 * Supported product types for facet fetching.
 */
export type FacetProductType = 'Watch' | 'Strap' | 'Eyewear';

/**
 * Get the locale-specific facet attributes for a product type and locale code.
 */
function getFacetAttributes(productType: FacetProductType, localeCode: string): string[] {
  if (productType === 'Strap') return getStrapFacets(localeCode);
  if (productType === 'Eyewear') return getEyewearFacets(localeCode);
  return getWatchFacets(localeCode);
}

/**
 * Probe the index to discover which Algolia locale code actually holds the
 * localized facet data, then cache the answer.
 *
 * The brand's locale config can select a different English variant than the
 * index stores (e.g. config -> `EN_GB`, index -> `EN_US`). Requesting the wrong
 * variant returns empty facets, which silently drops Case / Dial color / Type
 * filters from the UI. We request the localized facet attributes for every
 * candidate code in a single facets-only query and keep whichever variant the
 * index actually populated.
 */
async function probeAlgoliaLocaleCode(
  locale: string,
  productType: FacetProductType
): Promise<string> {
  const candidates = getLocaleCodeCandidates(locale);

  // No ambiguity (non-English locales): nothing to probe.
  if (candidates.length <= 1) return candidates[0] ?? getLocaleCode(locale);

  const client = getAlgoliaClient({ ssr: true });
  const indexName = getProductIndexName(locale);

  // Only the locale-dependent attributes (those carrying a `.{code}` suffix)
  // tell us which variant the index uses.
  const localeAttrsByCode = new Map<string, string[]>(
    candidates.map((code) => [
      code,
      getFacetAttributes(productType, code).filter((attr) => attr.includes('.')),
    ])
  );
  const requestedFacets = Array.from(new Set(Array.from(localeAttrsByCode.values()).flat()));

  const response = await client.searchSingleIndex<WatchHit | StrapHit>({
    indexName,
    searchParams: {
      query: '',
      filters: `product_type:${productType}`,
      facets: requestedFacets,
      hitsPerPage: 0,
      maxValuesPerFacet: 1,
    },
  });
  const facets = (response.facets as FacetResults) || {};

  // Pick the candidate whose localized attributes returned the most populated
  // facets. Candidates are ordered with the configured code first, so it wins ties.
  let best = candidates[0];
  let bestScore = -1;
  for (const code of candidates) {
    const score = (localeAttrsByCode.get(code) ?? []).filter(
      (attr) => facets[attr] && Object.keys(facets[attr]).length > 0
    ).length;
    if (score > bestScore) {
      bestScore = score;
      best = code;
    }
  }

  devLog(
    `[probeAlgoliaLocaleCode] ${locale}/${productType}: resolved to ${best} (score ${bestScore})`
  );
  return best;
}

const cachedProbeLocaleCode = unstable_cache(
  async (locale: string, productType: FacetProductType) =>
    probeAlgoliaLocaleCode(locale, productType),
  ['algolia-locale-code'],
  { revalidate: FACETS_REVALIDATE, tags: ['algolia', 'algolia-facets'] }
);

/**
 * In-memory request-level cache for resolved locale codes.
 */
const localeCodeCache = new Map<string, Promise<string>>();

/**
 * Resolve the Algolia locale code to use for localized facet attributes and
 * filter building for a given locale + product type.
 *
 * Returns the index-backed code (e.g. `EN_US`) rather than the brand's configured
 * code when they diverge, so facet display, counts, refinements and
 * `filterFacetsByConfig` all agree. Falls back to the configured code on error.
 *
 * Result is cached for an hour (and deduped per request).
 */
export function resolveAlgoliaLocaleCode(
  locale: string,
  productType: FacetProductType = 'Watch'
): Promise<string> {
  const cacheKey = `localeCode:${locale}:${productType}`;
  if (!localeCodeCache.has(cacheKey)) {
    localeCodeCache.set(
      cacheKey,
      cachedProbeLocaleCode(locale, productType).catch((error) => {
        devWarn('[resolveAlgoliaLocaleCode] Probe failed, using configured code:', error);
        return getLocaleCode(locale);
      })
    );
  }
  return localeCodeCache.get(cacheKey)!;
}

/**
 * Fetch facets directly from Algolia for a given locale and product type.
 */
async function fetchFacetsFromAlgolia(
  locale: string,
  productType: FacetProductType
): Promise<FacetResults> {
  const client = getAlgoliaClient({ ssr: true });
  // Use the index-backed locale code (handles config/index variant mismatch,
  // e.g. configured EN_GB vs index EN_US) so localized facets are not dropped.
  const localeCode = await resolveAlgoliaLocaleCode(locale, productType);
  const indexName = getProductIndexName(locale);

  const facetAttributes = getFacetAttributes(productType, localeCode);

  const filterString = buildFilterString({
    productType: productType,
    draftMode: false,
    localeCode,
  });

  logAlgoliaRequest('fetchFacetsFromAlgolia', {
    ...algoliaContextFields(locale, indexName),
    localeCode,
    productType,
    filters: filterString,
    facetAttributes,
  });

  const response = await client.searchSingleIndex<WatchHit | StrapHit>({
    indexName,
    searchParams: {
      query: '',
      filters: filterString,
      facets: facetAttributes,
      hitsPerPage: 0,
      maxValuesPerFacet: ALGOLIA_CONFIG.maxFacetValues,
    },
  });

  const facets = (response.facets as FacetResults) || {};

  logAlgoliaResponse('fetchFacetsFromAlgolia', {
    ...algoliaContextFields(locale, indexName),
    productType,
    processingTimeMs: response.processingTimeMS ?? 0,
    facetCount: Object.keys(facets).length,
    facetsWithValues: Object.fromEntries(
      Object.entries(facets).map(([name, values]) => [name, Object.keys(values).length])
    ),
  });

  return facets;
}

const cachedFetchFacets = unstable_cache(
  async (locale: string, productType: FacetProductType) => {
    return fetchFacetsFromAlgolia(locale, productType);
  },
  ['algolia-facets'],
  // Tags are not currently wired to any revalidation webhook — they exist so
  // we can call revalidateTag('algolia') programmatically if needed in the future.
  { revalidate: FACETS_REVALIDATE, tags: ['algolia', 'algolia-facets'] }
);

/**
 * Fetch facet values with time-based caching.
 *
 * Cached for 1 hour via unstable_cache.
 * During cache hit, this is essentially free (no Algolia call).
 *
 * @param locale - Locale string (e.g., 'us-en')
 * @param productType - Product type to fetch facets for (default: 'Watch')
 * @returns Facet results or null if fetch fails
 */
export async function getCachedFacets(
  locale: string,
  productType: FacetProductType = 'Watch'
): Promise<FacetResults | null> {
  try {
    devLog(`[getCachedFacets] Fetching for ${locale}/${productType}`);
    return await cachedFetchFacets(locale, productType);
  } catch (error) {
    devWarn('[getCachedFacets] Error:', error);
    return null;
  }
}

/**
 * In-memory cache for facets within a single request.
 * Prevents multiple fetches to the same locale/productType in one render.
 */
const requestCache = new Map<string, Promise<FacetResults | null>>();

/**
 * Get cached facets with request-level deduplication.
 *
 * If multiple components request the same locale's facets
 * in the same request, only one fetch is made.
 *
 * @param locale - Locale string (e.g., 'us-en')
 * @param productType - Product type to fetch facets for (default: 'Watch')
 */
export async function getCachedFacetsDeduped(
  locale: string,
  productType: FacetProductType = 'Watch'
): Promise<FacetResults | null> {
  const cacheKey = `facets:${locale}:${productType}`;

  if (!requestCache.has(cacheKey)) {
    requestCache.set(cacheKey, getCachedFacets(locale, productType));
  }

  return requestCache.get(cacheKey)!;
}
